import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import AITaskPill from "./AITaskPill";
import { SignOut, Key, User, Buildings, Briefcase, Calendar, Question, UsersThree, FileText, EnvelopeSimple, ChatCircleDots, IdentificationCard, ClipboardText, ShieldCheck, ShieldWarning, Receipt } from "@phosphor-icons/react";

export default function AppLayout({ children, title, breadcrumbs }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isJobCoach = user?.role === "job_coach";
  const isBedrijfsadmin = isJobCoach && user?.is_bedrijfsadmin === true;
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-[var(--c-bg)] flex">
      <aside className="w-64 shrink-0 border-r border-zinc-200 bg-white flex flex-col" data-testid="app-sidebar">
        <div className="px-6 py-6 border-b border-zinc-200">
          <Link to="/" className="flex items-center gap-2">
            <Briefcase weight="duotone" size={28} color="#1E88E5" />
            <div>
              <div className="font-display text-[17px] font-bold leading-tight tracking-tight">Werk Dat Past Online</div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">Coaching platform</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 text-sm">
          {isAdmin && (
            <>
              <NavLink to="/admin" data-testid="nav-admin" className={navClass}>
                <Buildings size={18} /> Bedrijven
              </NavLink>
              <NavLink to="/admin/facturatie" data-testid="nav-admin-billing" className={navClass}>
                <Receipt size={18} /> Facturatie
              </NavLink>
              <NavLink to="/admin/verwijderverzoeken" data-testid="nav-deletion-requests" className={navClass}>
                <ShieldWarning size={18} /> Verwijderverzoeken
              </NavLink>
            </>
          )}
          {isJobCoach && (
            <>
              <NavLink to="/dashboard" data-testid="nav-dashboard" className={navClass}>
                <UsersThree size={18} /> Mijn coachees
              </NavLink>
              {user?.company_id && (
                <NavLink to={`/companies/${user.company_id}`} end data-testid="nav-company" className={navClass}>
                  <Buildings size={18} /> {isBedrijfsadmin ? "Mijn bedrijf" : "Bedrijf"}
                </NavLink>
              )}
              {isBedrijfsadmin && user?.company_id && (
                <NavLink to={`/companies/${user.company_id}/facturatie`} data-testid="nav-company-billing" className={navClass}>
                  <Receipt size={18} /> Facturatie
                </NavLink>
              )}
              <NavLink to="/admin/verwijderverzoeken" data-testid="nav-deletion-requests" className={navClass}>
                <ShieldWarning size={18} /> Verwijderverzoeken
              </NavLink>
              <NavLink to="/settings" data-testid="nav-settings" className={navClass}>
                <Calendar size={18} /> Instellingen
              </NavLink>
            </>
          )}
          {user?.role === "coachee" && (
            <>
              <NavLink to="/cv" data-testid="nav-cv" className={navClass}>
                <FileText size={18} /> Mijn CV
              </NavLink>
              <NavLink to="/tests" data-testid="nav-tests" className={navClass}>
                <ClipboardText size={18} /> Tests
              </NavLink>
              <NavLink to="/profielen" data-testid="nav-profiles" className={navClass}>
                <IdentificationCard size={18} /> Profielen
              </NavLink>
              <NavLink to="/vacatures" data-testid="nav-vacatures" className={navClass}>
                <Briefcase size={18} /> Vacatures
              </NavLink>
              <NavLink to="/sollicitaties" data-testid="nav-cover-letter" className={navClass}>
                <EnvelopeSimple size={18} /> Sollicitaties
              </NavLink>
              <NavLink to="/interview" data-testid="nav-interview" className={navClass}>
                <ChatCircleDots size={18} /> Sollicitatiegesprek
              </NavLink>
              <NavLink to="/mijn-privacy" data-testid="nav-my-privacy" className={navClass}>
                <ShieldCheck size={18} /> Mijn privacy
              </NavLink>
            </>
          )}
          <NavLink to="/account" data-testid="nav-account" className={navClass}>
            <Key size={18} /> Wachtwoord wijzigen
          </NavLink>
          {!isAdmin && (
            <NavLink to="/help" data-testid="nav-help" className={navClass}>
              <Question size={18} weight="duotone" /> Help
            </NavLink>
          )}
        </nav>
        <div className="px-4 py-4 border-t border-zinc-200 text-sm">
          <div className="flex items-center gap-2 mb-3">
            <User size={18} className="text-zinc-500" />
            <div className="min-w-0">
              <div className="truncate font-medium" data-testid="sidebar-username">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500" data-testid="sidebar-role">
                {roleLabel(user)}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full inline-flex items-center justify-center gap-2 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors py-2 text-sm font-medium rounded-sm"
          >
            <SignOut size={16} /> Uitloggen
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="border-b border-zinc-200 bg-white px-8 py-5 sticky top-0 z-10">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              {breadcrumbs && breadcrumbs.length > 0 && (
                <nav className="text-xs text-zinc-500 mb-1 flex items-center gap-1.5 flex-wrap" data-testid="breadcrumbs">
                  {breadcrumbs.map((b, i) => (
                    <React.Fragment key={`${b.label}-${i}`}>
                      {b.to ? (
                        <Link to={b.to} className="hover:text-zinc-900 truncate">{b.label}</Link>
                      ) : (
                        <span className="truncate">{b.label}</span>
                      )}
                      {i < breadcrumbs.length - 1 && <span className="text-zinc-300">/</span>}
                    </React.Fragment>
                  ))}
                </nav>
              )}
              <h1 className="font-display text-2xl font-bold tracking-tight truncate" data-testid="page-title">{title}</h1>
            </div>
            {!isAdmin && (
              <Link
                to="/help"
                data-testid="header-help-button"
                title="Help"
                className="shrink-0 inline-flex items-center gap-2 px-3 py-2 border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 transition-colors rounded-sm text-sm font-medium"
              >
                <Question size={18} weight="duotone" />
                <span className="hidden sm:inline">Help</span>
              </Link>
            )}
          </div>
        </header>
        <main className="flex-1 px-8 py-8 min-w-0">{children}</main>
      </div>
      <AITaskPill />
    </div>
  );
}

function roleLabel(user) {
  if (!user) return "";
  if (user.role === "admin") return "Admin";
  if (user.role === "job_coach") return user.is_bedrijfsadmin ? "Bedrijfsadmin" : "Work coach";
  if (user.role === "coachee") return "Coachee";
  return user.role;
}

function navClass({ isActive }) {
  return (
    "flex items-center gap-2.5 px-3 py-2 rounded-sm transition-colors " +
    (isActive
      ? "bg-zinc-900 text-white"
      : "text-zinc-700 hover:bg-zinc-100")
  );
}
